/**
 * AuthMarketingPanel — the SINGLE shared left-side marketing panel used on
 * every unauthenticated surface: LoginPage, InviteAcceptPage, and — via
 * AuthLayout — the forgot/reset-password pages. One component means the brand
 * voice, value pillars, and night-green background can't drift between auth
 * surfaces.
 *
 * `showExploreLinks` (default true) toggles the public SEO explore links;
 * AuthLayout passes false so the password-reset flows stay task-focused.
 *
 * Near-pure presentation: the only state is a one-time effect that scatters
 * the background starfield (mirrors the landing sky). Consumers wrap it in
 * their own grid shell and provide the form column.
 */
import { useEffect, useRef } from 'react';
import AviumLogo from '../shared/AviumLogo';

interface AuthMarketingPanelProps {
  /**
   * Show the explore links (Learn the Signals · For your role · Why Avium).
   * Default true for marketing entry points (LoginPage, InviteAcceptPage);
   * AuthLayout's password-reset flows pass false — they're task-focused for an
   * existing account, not a marketing front door.
   */
  showExploreLinks?: boolean;
}

export default function AuthMarketingPanel({ showExploreLinks = true }: AuthMarketingPanelProps) {
  const starsRef = useRef<HTMLDivElement | null>(null);

  // Scatter the background starfield once, mirroring the landing page's sky:
  // ~120 stars at random positions/sizes, each twinkling on its own cadence.
  // Guarded against double-seeding (StrictMode remount) and reduced motion.
  useEffect(() => {
    const el = starsRef.current;
    if (!el || el.childElementCount) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    for (let i = 0; i < 120; i++) {
      const star = document.createElement('i');
      const depth = Math.random();
      star.style.left = `${Math.random() * 100}%`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.width = star.style.height = `${depth * 2 + 0.6}px`;
      star.style.opacity = String(0.25 + depth * 0.6);
      if (!reduce) {
        star.style.animation = `star-twinkle ${2.4 + Math.random() * 2.6}s ease-in-out ${Math.random() * 3}s infinite`;
      }
      el.appendChild(star);
    }
  }, []);

  return (
    <aside
      className="relative isolate flex flex-col text-white overflow-hidden p-8 sm:p-12 lg:p-[64px_72px]"
      style={{
        // Aligned with the V3 "Night Shift" landing page's night-green ground
        // (2026-07-08): the same near-black green stops + subtle teal bloom as
        // the homepage `.skyN` sky, so the public front door and the auth panel
        // read as one world. (Supersedes the 2026-06-11 V2 dark-teal alignment,
        // which ran a brighter teal at the bottom stop and diverged from the
        // landing after the Night Shift redesign.)
        background: `
          radial-gradient(circle at 80% 15%, rgba(20, 184, 166, 0.16) 0%, transparent 50%),
          linear-gradient(135deg, #020D0B 0%, #04211D 55%, #06302B 100%)
        `,
      }}
    >
      {/* Living chrome: drifting teal blooms + film grain under the content. */}
      <div aria-hidden className="aurora-layer" />
      {/* Starfield — the same scattered, twinkling night sky as the landing
          page (seeded by the effect above), replacing the old uniform dot grid. */}
      <div ref={starsRef} aria-hidden className="starfield" />

      <div className="relative flex-1 flex flex-col justify-center max-w-full lg:max-w-[520px]">
        {/* Brand row — the AviumLogo already provides its own squircle
            background, so no glass-tile wrapper needed here. */}
        <div className="flex items-center gap-3.5 mb-6 lg:mb-10">
          <AviumLogo size={52} variant="dark" className="flex-shrink-0" />
          <div className="text-2xl lg:text-[28px] font-extrabold tracking-tight">Avium</div>
        </div>

        {/* Headline — slogan rendered as three independent block lines so
            each phrase stands on its own. Orange gradient stays on the
            punch phrase (Catch the risk.). */}
        <h1 className="text-[26px] sm:text-[32px] lg:text-[40px] font-extrabold leading-[1.15] tracking-tight mb-3 lg:mb-4 space-y-1">
          <span className="block">Hit delivery dates</span>
          <span className="block" style={{ color: '#5EEAD4' }}>
            with confidence.
          </span>
        </h1>

        {/* Subheadline — AI anchor + three-pillar promise */}
        <p className="text-[15px] sm:text-base lg:text-lg leading-relaxed mb-4 lg:mb-6 max-w-full lg:max-w-[500px] font-medium" style={{ color: 'rgba(255, 255, 255, 0.92)' }}>
          You've been running them on gut feel. Avium delivers you answers every morning at <span className="font-bold text-white">7 am</span> — the risk, the odds on the date, and the one move that protects it.
        </p>

        {/* Body */}
        <p className="text-[13px] sm:text-sm lg:text-base leading-relaxed mb-6 lg:mb-10 max-w-full lg:max-w-[480px]" style={{ color: 'rgba(255, 255, 255, 0.72)' }}>
          One connection — Jira, Azure DevOps, Asana, or Excel — and Avium starts reading
          your delivery data in 60 seconds. Every claim drills to the ticket,
          grounded in your real data — never invented.
        </p>

        {/* Value pillars */}
        <div className="flex flex-col sm:flex-row lg:flex-col gap-2 sm:gap-3 lg:gap-[18px]">
          <Pillar
            title="Signals, not history"
            desc="Avium Signals sweep every ticket overnight and rank the risk — what's next, not what happened."
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            }
          />
          <Pillar
            title="The odds, not a feeling"
            desc="10,000 Monte-Carlo trials on your real throughput — the odds on the date, and the one move that lifts them."
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M9 21V9"/>
              </svg>
            }
          />
          <Pillar
            title="Grounded, not guessed"
            desc="Every claim wired to its ticket: signal → person → work → sprint — even blockers that aren't on your board."
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            }
          />
        </div>

        {/* Explore links — connect the landing page to the public SEO hubs so
            they're reachable from the front door, not only from a Google result.
            Hidden on the password-reset flows (AuthLayout passes
            showExploreLinks={false}) — those aren't marketing entry points. */}
        {showExploreLinks && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-6 text-sm">
            <a href="/learn" className="text-teal-200 hover:text-white transition-colors">Learn the Signals</a>
            <span className="text-white/20">·</span>
            <a href="/for" className="text-teal-200 hover:text-white transition-colors">For your role</a>
            <span className="text-white/20">·</span>
            <a href="/sales-brochure.html" target="_blank" rel="noopener noreferrer" className="text-teal-200 hover:text-white transition-colors">Why Avium ↗</a>
          </div>
        )}
      </div>

      {/* Punchline (hidden on tablet/mobile to save space) */}
      <div className="hidden lg:block relative mt-10 pt-7 text-[18px] font-semibold text-white tracking-tight leading-snug max-w-[540px]" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
        Sure — every dashboard and report is right there. But finding the risk shouldn't be your job. That's ours.
      </div>
    </aside>
  );
}

function Pillar({ title, desc, icon }: { title: string; desc: string; icon: React.ReactNode }) {
  return (
    <div
      className="flex items-center sm:items-start lg:items-start gap-3.5 p-3 lg:p-4 rounded-xl transition-all flex-1 sm:flex-col lg:flex-row"
      style={{
        // Frosted glass over the night-green ground (was solid white) so the
        // panel reads as one dark world, not white cards floating on green.
        background: 'rgba(255, 255, 255, 0.06)',
        border: '1px solid rgba(94, 234, 212, 0.18)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        className="w-8 h-8 lg:w-9 lg:h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background: 'rgba(94, 234, 212, 0.12)',
          border: '1px solid rgba(94, 234, 212, 0.28)',
          color: '#5EEAD4',
        }}
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-[13px] lg:text-sm font-bold tracking-tight leading-snug" style={{ color: '#FFFFFF' }}>
          {title}
        </div>
        <div className="text-[12px] lg:text-[13px] leading-snug mt-0.5 hidden lg:block sm:hidden" style={{ color: 'rgba(240, 253, 250, 0.72)' }}>
          {desc}
        </div>
      </div>
    </div>
  );
}
